import React from 'react';
import { useTranslation } from 'react-i18next';

import { LUMIO_DRAWER_ANIM } from '../../../../theme/tokens';
import { LumioChat } from '../LumioChat';
import { Root, Scrim, Panel } from './LumioDrawer.styles';

/**
 * Lumio assistant as a slide-in drawer — the mid-desktop counterpart to the
 * docked `LumioChat` column. On the `LUMIO_DRAWER_RANGE` (900–1279px) the
 * right-hand column is hidden, so the top bar's Lumio trigger opens this sheet
 * instead: the same `LumioChat` panel floating over a flat dimmed scrim, with
 * an iOS-style slide from the right (see `LUMIO_DRAWER_ANIM`).
 *
 * Open/close is a small mount machine: it mounts on `open`, paints the sheet
 * off-screen, then (after that frame is on screen) flips to the entered state so
 * the transition plays; on close it reverses, then unmounts after the slide
 * finishes (so the
 * panel — and a stray `LumioChat` instance — never lingers in the tree). Backdrop
 * click and Escape both close.
 */
export type LumioDrawerProps = {
  open: boolean;
  onClose: () => void;
};

export const LumioDrawer = ({ open, onClose }: LumioDrawerProps): React.ReactElement | null => {
  const { t } = useTranslation();
  // `mounted` keeps the sheet in the DOM through the close animation; `entered`
  // drives the enter/leave transition (off-screen ⇄ in place).
  const [mounted, setMounted] = React.useState(open);
  const [entered, setEntered] = React.useState(false);

  // Mount on open; keep the sheet mounted through the leave animation, then
  // unmount (so the panel — and a stray LumioChat — never lingers in the tree).
  React.useEffect(() => {
    if (open) {
      setMounted(true);
      return undefined;
    }
    setEntered(false);
    const timer = window.setTimeout(() => setMounted(false), LUMIO_DRAWER_ANIM.PANEL_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  // Play the slide-in only AFTER the off-screen sheet has actually painted. The
  // mount commits the panel at its off-screen transform; TWO rAFs guarantee the
  // browser paints that frame before we flip to "entered", so the transition has
  // a start point. A single rAF flipped in the same effect as the mount races the
  // first paint — the panel reaches "entered" before it ever paints off-screen,
  // and the slide-in just snaps (the slide-OUT is unaffected: the entered state is
  // already on screen, so it always has a start frame).
  React.useEffect(() => {
    if (!mounted || !open) return undefined;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [mounted, open]);

  // Escape closes while the drawer is on screen.
  React.useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mounted, onClose]);

  if (!mounted) return null;

  return (
    <Root $entered={entered} onClick={onClose} role="presentation" data-ftux-qa-lift="">
      <Scrim $entered={entered} aria-hidden="true" />
      <Panel
        $entered={entered}
        role="dialog"
        aria-modal="true"
        aria-label={t('Lumio')}
        onClick={(event) => event.stopPropagation()}
      >
        {/* The drawer only mounts when open, so its panel is always the active surface. Its collapse button
            sits in the panel header, after the "…" menu. */}
        <LumioChat active={open} onClose={onClose} />
      </Panel>
    </Root>
  );
};

export default LumioDrawer;
